import { useCallback, useMemo, useState } from "react";
import { isMirKvSource } from "../utils/mirkv";
import type { Cancellation } from "./useCancellations";

/**
 * DC «Отмены» — фильтр, поиск и сортировка поверх УЖЕ загруженного списка
 * (11.09, Даша через владельца: «чтобы я могла сама выбрать по дате, либо по
 * источнику бронирования и найти игру, либо сама буду вбивать имя дату время»).
 *
 * Почему без сервера: имя и телефон лежат в базе шифротекстом и расшифровываются
 * только на выходе из воркера (decryptFields в /cancelled) — искать по ним в SQL
 * физически нечем. На клиенте строки уже открыты, поэтому и поиск, и фильтры
 * считаются здесь. Область поиска = период календаря шапки, об этом честно
 * написано в пустом состоянии.
 */
export type CancelSort = "date" | "cancelled";
export type CancelDir = "asc" | "desc";

/** Тип записи: у ДК в отменах вперемешку игры, заявки и блокировки слота. */
export const TYPE_OPTS: { id: string; label: string }[] = [
  { id: "booking", label: "Брони" },
  { id: "lead", label: "Заявки" },
  { id: "block", label: "Блоки" },
];

export interface FilterOpt { id: string; label: string; count: number }

const NO_SOURCE = "\u0000none";

/**
 * Источник словами. В базе это свободный текст с хвостами разметки
 * («Сайт:universequest.ru|referrer=…|page=…»), и без свёртки список фильтра
 * превращается в простыню из ссылок. Сворачиваем до того, что человек
 * называет источником: звонок, сайт (с доменом — сайтов у сети несколько),
 * агрегатор, мессенджер. Заказ с меткой агрегатора относим к агрегатору —
 * канон isMirKvSource, тот же, по которому игра получает рамку в сетке.
 */
export function srcGroup(raw: unknown): string {
  const s = String(raw ?? "").trim();
  if (!s) return "Без источника";
  if (isMirKvSource(s)) return "Мир Квестов";
  const head = (s.split(/[:|·]/)[0] ?? s).trim();
  if (/^сайт$/i.test(head)) {
    const host = (s.split(":")[1] ?? "").split("|")[0]?.trim();
    return host ? `Сайт ${host}` : "Сайт";
  }
  if (/^звонок/i.test(head)) return "Звонок";
  return head.length > 18 ? `${head.slice(0, 17)}…` : head;
}

/** Регистр и «ё» не должны мешать: «Алёна» ищется по «алена». */
function norm(s: unknown): string {
  return String(s ?? "").toLowerCase().replace(/ё/g, "е");
}

/** Всё, по чему ищем в одной строке: имя, телефон, даты, время, квест, причина. */
function haystack(c: Cancellation): string {
  const date = String(c.date ?? "");
  const [y, m, d] = date.split("-");
  const human = m && d ? `${d}.${m} ${d}.${m}.${y ?? ""}` : "";
  const time = String(c.custom_time || c.time || "").slice(0, 5);
  const phone = String(c.phone ?? "");
  return norm([
    c.client_name, phone, phone.replace(/\D/g, ""),
    date, human, time, time.replace(":", ""),
    c.quest_name, c.short_name, c.branch_name,
    c.source, c.cancel_reason, c.cancelled_by_name,
    String(c.cancelled_at ?? "").slice(0, 10),
  ].join(" "));
}

/** Сортировочный ключ: дата+время игры либо момент отмены. */
function keyOf(c: Cancellation, sort: CancelSort): string {
  if (sort === "cancelled") return String(c.cancelled_at ?? "");
  return `${String(c.date ?? "")} ${String(c.custom_time || c.time || "")}`;
}

/** Список значений поля с числом попаданий — пункты дропдауна строим по факту. */
function optsOf(items: Cancellation[], pick: (c: Cancellation) => string, empty: string): FilterOpt[] {
  const seen = new Map<string, number>();
  for (const c of items) {
    const raw = pick(c).trim();
    const id = raw || NO_SOURCE;
    seen.set(id, (seen.get(id) ?? 0) + 1);
  }
  return [...seen.entries()]
    .map(([id, count]) => ({ id, label: id === NO_SOURCE ? empty : id, count }))
    .sort((a, b) => b.count - a.count);
}

export function useCancelFilter(items: Cancellation[]) {
  const [query, setQuery] = useState("");
  const [types, setTypes] = useState<Set<string>>(new Set());
  const [sources, setSources] = useState<Set<string>>(new Set());
  const [branches, setBranches] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<CancelSort>("date");
  const [dir, setDir] = useState<CancelDir>("desc");

  const sourceOpts = useMemo(() => optsOf(items, (c) => srcGroup(c.source), "Без источника"), [items]);
  const branchOpts = useMemo(() => optsOf(items, (c) => String(c.branch_name ?? ""), "Без филиала"), [items]);
  const typeOpts = useMemo<FilterOpt[]>(() => {
    const n = new Map<string, number>();
    for (const c of items) n.set(String(c.type ?? "booking"), (n.get(String(c.type ?? "booking")) ?? 0) + 1);
    return TYPE_OPTS.filter((t) => n.has(t.id)).map((t) => ({ ...t, count: n.get(t.id) ?? 0 }));
  }, [items]);

  const shown = useMemo(() => {
    /* Пробелы делят запрос на условия, каждое обязано найтись: «аня 19:30». */
    const words = norm(query).split(/\s+/).filter(Boolean);
    const rows = items.filter((c) => {
      if (types.size && !types.has(String(c.type ?? "booking"))) return false;
      if (sources.size && !sources.has(srcGroup(c.source))) return false;
      if (branches.size && !branches.has(String(c.branch_name ?? "").trim() || NO_SOURCE)) return false;
      if (!words.length) return true;
      const hay = haystack(c);
      return words.every((w) => hay.includes(w));
    });
    const sorted = [...rows].sort((a, b) => {
      const ka = keyOf(a, sort), kb = keyOf(b, sort);
      /* Записи без времени отмены держим в конце при любом направлении. */
      if (!ka !== !kb) return ka ? -1 : 1;
      return dir === "desc" ? kb.localeCompare(ka) : ka.localeCompare(kb);
    });
    return sorted;
  }, [items, query, types, sources, branches, sort, dir]);

  /* Повторный тап по активной сортировке переворачивает её (канон «Клиентов»).
     Оба сеттера вызываем рядом: setDir внутри updater'а setSort React в
     строгом режиме прогонял бы дважды и переворот гасил сам себя. */
  const pickSort = useCallback((k: CancelSort) => {
    if (k === sort) setDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSort(k); setDir("desc"); }
  }, [sort]);

  const reset = useCallback(() => {
    setQuery(""); setTypes(new Set()); setSources(new Set()); setBranches(new Set());
  }, []);

  const activeCount = (query.trim() ? 1 : 0) + (types.size ? 1 : 0) + (sources.size ? 1 : 0) + (branches.size ? 1 : 0);

  return {
    query, setQuery, types, setTypes, sources, setSources, branches, setBranches,
    sort, dir, pickSort, shown, typeOpts, sourceOpts, branchOpts, activeCount, reset,
    noSourceId: NO_SOURCE,
  };
}
